import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Alert, buttonVariants, Card, Chip, Table } from "@heroui/react";
import { LEVEL_ATTRIBUTE_KEY, ROLE_ATTRIBUTE_KEY } from "@vlastiq/contracts";
import { useAuth } from '../auth/AuthProvider.tsx';
import {
	adminMoveLootToEnd,
	adminMoveLootToFront,
	adminRecordLootCollected,
	adminRecordLootSkipped,
	fetchLootQueue,
	fetchCatalogue,
	fetchMyCharacter,
	fetchRoster,
	fetchSchedule,
	type Character,
	type LootMutationResponse,
	type LootQueueEntry,
	type Occurrence,
	type RosterEntry,
} from "../data/api.ts";
import { LootQueue, notifyLootAnnouncement } from "../components/LootQueue.tsx";
import {
	KarmaBadge,
	KarmaGiveDialog,
	KarmaHistoryDialog,
	KarmaRowMenu,
	type KarmaTarget,
} from "../components/Karma.tsx";
import { ScheduleTable } from "../components/ScheduleTable.tsx";
import { InlineLoader } from "../components/InlineLoader.tsx";
import { ActivityCard } from "../components/ActivityCard.tsx";
import { groupByDay, type ScheduleItem } from "../lib/schedule.ts";
import { karmaControl } from "../lib/karma.ts";
import { useAction } from "../lib/useAction.ts";
import { errorText } from "../lib/errors.ts";

/** Matches the schedule page's tick; see the `now` state below. */
const CLOCK_TICK_MS = 30_000;

/**
 * A character's level, as a number, or null.
 *
 * Level is an ATTRIBUTE, not a column — the catalogue is admin-seeded
 * (DECISION-9), so this deployment may have no `level` attribute at all, or
 * have one that somebody defined as text. Both are ordinary states, not
 * errors: coerce what can be coerced and treat everything else as absent.
 */
function levelOf(entry: RosterEntry): number | null {
	const raw = entry.values[LEVEL_ATTRIBUTE_KEY];
	if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
	if (typeof raw === "string" && raw.trim() !== "") {
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

/**
 * A character's role, as a label, or null.
 *
 * Deliberately type-agnostic where `levelOf` is strict: a role is a LABEL, so
 * whatever the admin chose for the attribute — free text, a single select, even
 * a multi-select — renders the same way. An array is joined rather than
 * stringified, because `String(['Танк','Хил'])` is «Танк,Хил» with no space.
 */
function roleOf(entry: RosterEntry): string | null {
	const raw = entry.values[ROLE_ATTRIBUTE_KEY];
	if (Array.isArray(raw)) {
		const parts = raw.filter((part) => typeof part === "string" && part.trim() !== "");
		return parts.length === 0 ? null : parts.join(", ");
	}
	if (typeof raw === "string") return raw.trim() === "" ? null : raw.trim();
	if (typeof raw === "number") return String(raw);
	return null;
}

/**
 * Highest level first, then by character name.
 *
 * `numeric: true` is what makes it alphaNUMERIC rather than alphabetical:
 * without it «Melkor10» sorts before «Melkor2», because a plain string
 * comparison reads the '1' and stops. `sensitivity: 'base'` keeps case and
 * accents out of it, so «melkor» and «Melkor» do not straddle another name.
 *
 * A character with NO level sorts last rather than as zero. Nobody is level
 * zero; the field is simply empty, and putting them at the bottom says that
 * without claiming anything.
 */
function byLevelThenName(a: RosterEntry, b: RosterEntry): number {
	const left = levelOf(a);
	const right = levelOf(b);
	if (left !== right) {
		if (left === null) return 1;
		if (right === null) return -1;
		return right - left;
	}
	return a.character.name.localeCompare(b.character.name, "ru", {
		numeric: true,
		sensitivity: "base",
	});
}

/** «Марина Л.» / «@marina_l2» — whichever of the two Telegram has. */
function ownerOf(entry: RosterEntry): {
	name: string | null;
	handle: string | null;
} {
	const name = [entry.memberFirstName, entry.memberLastName]
		.filter(Boolean)
		.join(" ");
	return {
		name: name === "" ? null : name,
		handle: entry.memberUsername === null ? null : `@${entry.memberUsername}`,
	};
}

/** Shared landing page — visible to everyone (DECISION-2). */
export function DashboardPage() {
	const { telegramUserId, isAdmin } = useAuth();
	const [character, setCharacter] = useState<Character | null>(null);
	const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
	/**
	 * Re-read on a timer, for the same reason the schedule page does: `now` is
	 * read once per render, and a landing page left open on a phone renders once
	 * — so «идёт» would never light up and a finished raid would never dim.
	 */
	const [now, setNow] = useState(() => Date.now());
	const [roster, setRoster] = useState<RosterEntry[] | null>(null);
	const [rosterError, setRosterError] = useState<string | null>(null);
	const [queue, setQueue] = useState<LootQueueEntry[] | null>(null);
	/**
	 * The LOAD's failure only.
	 *
	 * ⚠️ IT USED TO CARRY THE WRITES' FAILURES TOO, which meant a loot action
	 * that failed replaced the sentence explaining why the queue was empty — two
	 * unrelated facts sharing one slot, and the more useful one losing.
	 */
	const [queueError, setQueueError] = useState<string | null>(null);
	const action = useAction();
	const [loaded, setLoaded] = useState(false);
	/**
	 * Whether this deployment HAS a level attribute at all.
	 *
	 * ⚠️ NOT the same question as "does anybody have a level", and conflating
	 * them is what produced a column of «—» nobody could explain. The catalogue
	 * is admin-seeded and ships EMPTY (DECISION-9), so until somebody creates an
	 * attribute keyed `level` the column cannot ever hold a value — and a column
	 * that cannot hold a value should not be drawn at all, rather than drawn
	 * full of dashes that look like missing data.
	 */
	const [hasLevelAttribute, setHasLevelAttribute] = useState(true);
	/** Same question for `role`, and answered the same way. */
	const [hasRoleAttribute, setHasRoleAttribute] = useState(true);

	useEffect(() => {
		// allSettled, not all: four independent panels, and one failing request
		// used to blank the others despite the comment below claiming otherwise.
		void Promise.allSettled([
			fetchMyCharacter(),
			// One day is all this card shows. The window opens at MOSCOW MIDNIGHT
			// server-side, so `days=1` is the whole of today — including what has
			// already happened — plus the start of tomorrow, which `groupByDay`
			// then files under its own date and this page ignores.
			fetchSchedule(1),
			fetchRoster(),
			fetchLootQueue(),
			fetchCatalogue(),
		]).then(([mine, schedule, characters, loot, catalogue]) => {
			/*
			  Defaults to TRUE, so a failed catalogue request leaves the column
			  showing rather than silently removing it. Losing a column because a
			  secondary request timed out is a worse failure than a column of
			  dashes — the dashes at least look like data that is missing.
			*/
			if (catalogue.status === "fulfilled") {
				const has = (key: string) =>
					catalogue.value.character.some((def) => def.key === key && def.isActive);
				setHasLevelAttribute(has(LEVEL_ATTRIBUTE_KEY));
				setHasRoleAttribute(has(ROLE_ATTRIBUTE_KEY));
			}
			// Individual panels degrade to their empty state; the guard already
			// handles the cases that matter (not a member, cannot verify).
			if (mine.status === "fulfilled") setCharacter(mine.value.character);
			if (schedule.status === "fulfilled")
				setOccurrences(schedule.value.occurrences);
			if (characters.status === "fulfilled") {
				setRoster(characters.value.characters);
			} else {
				setRosterError(errorText(characters.reason, "Не удалось загрузить состав."));
			}
			if (loot.status === "fulfilled") {
				setQueue(loot.value.queue);
			} else {
				setQueueError(errorText(loot.reason, "Не удалось загрузить ротацию."));
			}
			setLoaded(true);
		});
	}, []);

	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
		return () => clearInterval(id);
	}, []);

	/**
	 * One runner for all three queue writes.
	 *
	 * ⚠️ THE NEW QUEUE COMES OUT OF THE WRITE'S OWN RESPONSE, not out of a
	 * follow-up GET. Every loot mutation answers with the whole queue exactly so
	 * that a second request cannot land first and redraw the list from before the
	 * change — which on this page would look like the click having done nothing,
	 * and invite a second one that moves somebody twice.
	 *
	 * The announcement is surfaced whatever it says. `posted: false` is not an
	 * error in four of its five forms; `notifyLootAnnouncement` is what decides
	 * how loudly each one reads — the same function Админка → Ротация лута calls,
	 * so the two screens cannot disagree about it.
	 *
	 * The one-write-at-a-time rule that used to be `if (busyCharacterId !== null)
	 * return` here now lives in `useAction`, with the argument written out on it.
	 */
	function runLootAction(characterId: number, write: () => Promise<LootMutationResponse>) {
		void action.run(
			`row:${characterId}`,
			async () => {
				const result = await write();
				setQueue(result.queue);
				notifyLootAnnouncement(result.announcement);
			},
			// No `success`: the announcement IS the outcome, and a «готово»
			// stacked over «бот промолчал — в ротации пока никого» says less than
			// the message it covers.
			{ error: "Не удалось изменить очередь." },
		);
	}

	/**
	 * Fold a karma change back into the roster already on screen (DECISION-37).
	 *
	 * ⚠️ PATCHED IN PLACE RATHER THAN REFETCHED, unlike the loot writes just
	 * above — and the difference is what each response contains. A loot mutation
	 * answers with the WHOLE queue precisely because it reorders everybody; a
	 * karma grant changes exactly one row's two numbers, and re-fetching the
	 * roster to learn them would redraw the table under whoever just pressed the
	 * heart, for no new information.
	 *
	 * `remainingToday` is optional because the two callers know different things:
	 * granting reports both numbers, while an admin DELETING an entry lowers the
	 * total and leaves the giver's daily allowance alone to be re-read on the
	 * next load. Guessing it here would be inventing a number.
	 */
	function applyKarma(characterId: number, karma: number, remainingToday?: number) {
		setRoster((current) =>
			current === null
				? current
				: current.map((entry) =>
						entry.character.id === characterId
							? {
									...entry,
									karma,
									karmaRemainingToday: remainingToday ?? entry.karmaRemainingToday,
								}
							: entry,
					),
		);
	}

	/** Which row has a write in flight. This page has no queue-wide action. */
	const busyCharacterId =
		action.pending?.startsWith("row:") === true ? Number(action.pending.slice(4)) : null;

	const sorted = roster === null ? null : [...roster].sort(byLevelThenName);

	// The card shows TODAY and nothing else, so an empty schedule and a schedule
	// whose next entry is tomorrow are the same state here — both get «на сегодня
	// ничего». Reading `days[0]` instead would silently promote tomorrow's raid
	// under a heading that says «сегодняшнее».
	const today = groupByDay(occurrences, now).find((day) => day.isToday)?.items ?? [];

	return (
		<div className="fade-in mx-auto flex w-full max-w-3xl flex-col gap-4">
			{/*
			  «О вас» USED TO BE HERE and moved to Мой персонаж (2026-08-12, owner's
			  call). It was the first card on the landing page and it told you three
			  things you already knew — your own handle, your own role, your own
			  character's name — above the two lists everybody actually comes for.
			  Its natural home is the page about you.
			*/}

			<TodayCard items={today} loaded={loaded} />

			<CharacterList
				entries={sorted}
				error={rosterError}
				loaded={loaded}
				hasOwnCharacter={character !== null}
				ownMemberId={telegramUserId}
				hasLevelAttribute={hasLevelAttribute}
				hasRoleAttribute={hasRoleAttribute}
				isAdmin={isAdmin}
				onKarmaChanged={applyKarma}
			/>

			{/* Under the character list, not above it: the rotation is a fact ABOUT
			    the people in that list, and it reads as one when it follows them. */}
			<LootRotationCard
				entries={queue}
				error={queueError}
				loaded={loaded}
				isAdmin={isAdmin}
				busyCharacterId={busyCharacterId}
				ownMemberId={telegramUserId}
				onMoveToFront={(id) => runLootAction(id, () => adminMoveLootToFront(id))}
				onMoveToEnd={(id) => runLootAction(id, () => adminMoveLootToEnd(id))}
				onCollected={(id) => runLootAction(id, () => adminRecordLootCollected(id))}
				onSkipped={(id) => runLootAction(id, () => adminRecordLootSkipped(id))}
			/>

			{/*
				LAST ON THE PAGE, and that is where it belongs rather than a
				compromise. The two lists above answer «что сегодня» and «чья
				очередь» — questions people open the app to settle. This one
				answers «кто вообще здесь есть», which is worth knowing and
				nobody is waiting on.
			*/}
			<ActivityCard ownMemberId={telegramUserId} />
		</div>
	);
}

/**
 * Today's whole schedule, on the home page.
 *
 * ⚠️ THE SAME `ScheduleTable` THE Расписание PAGE USES, not a summary of it
 * (2026-08-12, owner's call, replacing «Ближайшая активность»). The old card
 * named one event and its time; the question people open this page with is what
 * is on TODAY, and answering it with the first row of the list forced a second
 * navigation to see whether anything followed it.
 *
 * Everything today, including what is already over — dimmed rather than
 * dropped. A day that has begun is still the day you are being shown, and a
 * card that quietly starts at «now» cannot be read as a plan for the evening.
 */
function TodayCard({
	items,
	loaded,
}: {
	items: ScheduleItem<Occurrence>[];
	loaded: boolean;
}) {
	return (
		<Card>
			<Card.Header>
				<Card.Title>Сегодняшнее расписание</Card.Title>
			</Card.Header>
			<Card.Content className="flex flex-col gap-4">
				{!loaded && <InlineLoader />}

				{loaded && items.length === 0 && (
					<p className="text-sm text-muted">На сегодня ничего не запланировано.</p>
				)}

				{items.length > 0 && (
					<ScheduleTable items={items} ariaLabel="Сегодняшнее расписание" />
				)}

				{/* Kept from «Ближайшая активность»: this card is deliberately one
				    day, so the way to the other twenty has to stay visible. */}
				<p>
					<Link
						to="/schedule"
						className="text-sm text-link underline underline-offset-2"
					>
						Всё расписание
					</Link>
				</p>
			</Card.Content>
		</Card>
	);
}

/**
 * The loot rotation, on the home page (DECISION-33).
 *
 * Everyone sees it; only an admin gets the actions column. That is a change
 * from the holding position the old derived rotation was parked in — it was
 * admin-only, so members could not see whose turn it was anywhere except in a
 * bot message. The queue is the answer to a question the whole clan asks.
 *
 * `isAdmin` here decides what to RENDER and nothing else. The server re-checks
 * membership and role on every one of these writes; a cookie claiming admin
 * gets member access and the live answer wins.
 */
function LootRotationCard({
	entries,
	error,
	loaded,
	isAdmin,
	busyCharacterId,
	ownMemberId,
	onMoveToFront,
	onMoveToEnd,
	onCollected,
	onSkipped,
}: {
	entries: LootQueueEntry[] | null;
	error: string | null;
	loaded: boolean;
	isAdmin: boolean;
	busyCharacterId: number | null;
	ownMemberId: number | null;
	onMoveToFront: (characterId: number) => void;
	onMoveToEnd: (characterId: number) => void;
	onCollected: (characterId: number) => void;
	onSkipped: (characterId: number) => void;
}) {
	const next = entries?.[0] ?? null;

	return (
		<Card>
			<Card.Header>
				<Card.Title>Ротация лута</Card.Title>
			</Card.Header>

			<Card.Content className="flex flex-col gap-4">
				{error && <Alert status="danger">{error}</Alert>}
				{!loaded && !error && <InlineLoader />}

				{/* Stated outright rather than left implicit in the ordering. "Чья
				    очередь" is the question this table exists to answer, and reading
				    it off row 1 is a step the reader should not have to take. */}
				{next && (
					<p>
						Следующий:{" "}
						<strong className="font-medium">{next.characterName}</strong>
						<span className="text-muted">
							{next.lastCollectedAt
								? ` — последний раз забирал ${new Date(
										next.lastCollectedAt,
									).toLocaleDateString("ru-RU")}`
								: " — ни разу не забирал"}
						</span>
					</p>
				)}

				{entries?.length === 0 && (
					<p className="text-sm text-muted">В ротации пока никого.</p>
				)}

				{/*
				  The way out to the controls this card deliberately does not carry.

				  ⚠️ THE ROTATION IS READ HERE AND MANAGED ELSEWHERE, which is right:
				  everybody needs to see whose turn it is, and almost nothing needs
				  managing. Two clicks away and unsignposted is the same as absent,
				  which is how the missing route was reported in the first place.

				  It no longer says «и персонажи без Telegram» — those moved to
				  Админка → Состав, and the character list above has its own link
				  to them.

				  Admins only. A member following this link would land on a page the
				  route guard bounces them off.
				*/}
				{isAdmin && loaded && (
					<p>
						<Link
							to="/admin/loot"
							className="text-sm text-link underline underline-offset-2"
						>
							Управление ротацией
						</Link>
					</p>
				)}

				{/*
				  ⚠️ NO `dragAndDropHooks` HERE, AND THAT IS DELIBERATE TWICE OVER
				  (DECISION-43). Reordering the rotation is a management task and this
				  card links to the page that does it — and calling `useDragAndDrop` on
				  the home page would put react-aria's whole drag machinery into the
				  bundle every MEMBER downloads, to power a control only admins can
				  use. The menu items are here; the gesture is not.
				*/}
				{entries && entries.length > 0 && (
					<LootQueue
						entries={entries}
						isAdmin={isAdmin}
						busyCharacterId={busyCharacterId}
						onMoveToFront={onMoveToFront}
						onMoveToEnd={onMoveToEnd}
						onCollected={onCollected}
						onSkipped={onSkipped}
						ownMemberId={ownMemberId}
					/>
				)}

				{/* The announcement notice is a toast now — see
				    `notifyLootAnnouncement`, which is where the tone rule that used to
				    be duplicated in this branch and Админка → Ротация лута lives. */}

				{isAdmin && (
					<p className="text-sm text-muted">
						Очередь двигается только вручную и в группу об этом ничего не
						уходит. «В начало очереди» и «В конец очереди» — это план на
						будущее, в историю они не пишутся; «Собрал лут» и «Не явился»
						записываются и отправляют персонажа в конец. Переставить кого-то на
						произвольное место и объявить очередь вслух можно в Админке.
					</p>
				)}
			</Card.Content>
		</Card>
	);
}

/**
 * Everyone's characters (DECISION-2 — the roster is shared).
 *
 * This was its own page until the character list became the thing the home
 * page is for. Two columns only: who the character is, and how far along they
 * are. Anything else belongs on the character's own page.
 */
function CharacterList({
	entries,
	error,
	loaded,
	hasOwnCharacter,
	ownMemberId,
	hasLevelAttribute,
	hasRoleAttribute,
	isAdmin,
	onKarmaChanged,
}: {
	entries: RosterEntry[] | null;
	error: string | null;
	loaded: boolean;
	hasOwnCharacter: boolean;
	ownMemberId: number | null;
	/** False when no active attribute is keyed `level` — see the state above. */
	hasLevelAttribute: boolean;
	/** Same, for `role`. */
	hasRoleAttribute: boolean;
	isAdmin: boolean;
	onKarmaChanged: (characterId: number, karma: number, remainingToday?: number) => void;
}) {
	/*
	  ⚠️ ONE DIALOG STATE FOR THE WHOLE TABLE, not one per row (DECISION-37).
	  Forty rows means forty modals in the DOM of a page whose job is to render a
	  table, and react-aria mounts a focus scope and a scroll lock for each. The
	  target carries the NAME as well as the id so the dialog can be headed
	  without looking the row up again.
	*/
	const [historyFor, setHistoryFor] = useState<KarmaTarget | null>(null);
	const [giveTo, setGiveTo] = useState<KarmaTarget | null>(null);

	const targetOf = (entry: RosterEntry): KarmaTarget => ({
		characterId: entry.character.id,
		characterName: entry.character.name,
	});
	/*
	  ⚠️ THE COLUMN SET IS COMPUTED, NOT DECLARED, and the header and the body
	  both iterate THIS array.

	  It was two hardcoded columns with `hasLevelAttribute &&` written twice —
	  once in the header, once in the row — which is the shape every other table
	  here carries a warning about: declaring N columns and hiding one puts an
	  N-cell header over (N-1)-cell rows, and every value then sits under the
	  wrong heading. Two conditionals that must agree is a rule somebody has to
	  remember; one array is a rule that cannot be broken.
	*/
	const columns: {
		id: string;
		header: string;
		className?: string;
		isRowHeader?: boolean;
		cell: (entry: RosterEntry) => ReactNode;
	}[] = [
		{
			id: 'character',
			header: 'Персонаж',
			isRowHeader: true,
			cell: (entry) => (
				<CharacterIdentity
					entry={entry}
					isOwn={isOwnCharacter(entry, ownMemberId)}
					onOpenKarma={() => setHistoryFor(targetOf(entry))}
				/>
			),
		},
		...(hasRoleAttribute
			? [
					{
						id: 'role',
						header: 'Роль',
						/*
						  Narrow, nowrap, and `px-1` rather than HeroUI's `px-4`.
						  It was `px-2` while this table had three columns — the
						  default padding alone is 96px of a 320px screen — and the
						  fourth column bought the other 4px a side back. Measured
						  against the real clan roster: 318px at 390, i.e. it FITS,
						  where `px-2` left it 8px over.
						*/
						className: 'w-0 whitespace-nowrap px-1',
						cell: (entry: RosterEntry) => <RoleValue entry={entry} />,
					},
				]
			: []),
		...(hasLevelAttribute
			? [
					{
						id: 'level',
						// «Ур.», not «Уровень» (owner's call, 2026-08-12): with a third
						// column the full word was the widest thing in it, and a
						// heading is not worth the pixels a name needs to stay on one
						// line.
						header: 'Ур.',
						// `px-1`, for the reason on «Роль» above.
						className: 'w-0 whitespace-nowrap px-1',
						cell: (entry: RosterEntry) => <LevelValue entry={entry} />,
					},
				]
			: []),
	];

	/*
	  ⚠️ THE ROW ACTIONS RIDE AT THE END OF THE LAST COLUMN, and do not get one
	  of their own (owner's call, twice — see `KarmaRowMenu`).

	  WHICH column that is depends on the catalogue: «Ур.» normally, «Роль» if
	  this deployment has no level attribute, and the character cell itself if it
	  has neither. Wrapping whichever it is keeps the menu at the right-hand end
	  of the row in all three cases, where hard-coding «Ур.» would silently drop
	  the actions in a deployment that never seeded one.

	  `flex-1` on the original content is what makes the same wrapper work for a
	  `w-0` value column and for the wide character cell: in the narrow one there
	  is no slack to grow into and the menu simply follows the value; in the wide
	  one the identity fills the row and the menu is pushed to the edge.
	*/
	const last = columns[columns.length - 1];
	if (last) {
		const inner = last.cell;
		columns[columns.length - 1] = {
			...last,
			cell: (entry: RosterEntry) => (
				<div className="flex items-center gap-1">
					<div className="min-w-0 flex-1">{inner(entry)}</div>
					<KarmaRowMenu
						control={karmaControl(entry, ownMemberId)}
						characterName={entry.character.name}
						onGive={() => setGiveTo(targetOf(entry))}
					/>
				</div>
			),
		};
	}

	/*
	  Which of the two special keys the catalogue is missing.
	
	  Said only to ADMINS, and only when there is something they can do about it:
	  a member cannot create an attribute, so telling them the catalogue lacks one
	  is noise about somebody else's job — and the list still works, it is simply
	  sorted by name.
	*/
	const missingColumns = [
		{ key: ROLE_ATTRIBUTE_KEY, label: 'Роль', present: hasRoleAttribute },
		{ key: LEVEL_ATTRIBUTE_KEY, label: 'Уровень', present: hasLevelAttribute },
	].filter((column) => !column.present);

	return (
		<Card>
			<Card.Header>
				<Card.Title>Состав клана</Card.Title>
			</Card.Header>

			<Card.Content className="flex flex-col gap-4">
				{error && <Alert status="danger">{error}</Alert>}
				{!loaded && !error && <InlineLoader />}

				{entries?.length === 0 && (
					<p className="text-sm text-muted">Пока никто не добавил персонажа.</p>
				)}

				{/*
				  Said only to ADMINS, and only when there is something they can do
				  about it. A member cannot create an attribute, so telling them the
				  catalogue is missing one is noise about somebody else's job — and
				  the list still works, it is simply sorted by name.
				*/}
				{/* One sentence covering BOTH special keys rather than one block per
				    column. Two nearly identical paragraphs stacked on a phone say the
				    same thing twice and bury the link they share. */}
				{loaded && isAdmin && missingColumns.length > 0 && (
					<p className="text-sm text-muted">
						{missingColumns.length === 1 ? "Колонка" : "Колонки"}{" "}
						{missingColumns.map((column, index) => (
							<span key={column.key}>
								{index > 0 && " и "}
								«{column.label}» (ключ{" "}
								<strong className="text-foreground">{column.key}</strong>)
							</span>
						))}{" "}
						{missingColumns.length === 1 ? "скрыта" : "скрыты"}: в каталоге нет такого
						активного атрибута. Заведите его в{" "}
						<Link
							to="/admin/attributes"
							className="text-link underline underline-offset-2"
						>
							Админка → Атрибуты
						</Link>{" "}
						— ключ должен быть именно таким.
					</p>
				)}

				{entries && entries.length > 0 && (
					/*
					  ONE table at every width, and no card list beneath it.
					
					  Every other table in the app used to swap to a card-per-row layout
					  below `md:`. That is the conventional answer and it is the wrong one
					  here: this app is used mostly on a phone, and a card list preserves
					  rows while destroying columns — you can no longer compare two
					  characters' levels by running your eye down one edge, which is the
					  entire question this list answers. `Table.ScrollContainer` gives it
					  a horizontal scroll on a narrow screen instead.
					*/
					<Table>
						<Table.ScrollContainer>
							<Table.Content aria-label="Состав клана">
								<Table.Header>
									{columns.map((column) => (
										<Table.Column
											key={column.id}
											id={column.id}
											isRowHeader={column.isRowHeader}
											className={column.className}
										>
											{column.header}
										</Table.Column>
									))}
								</Table.Header>
								<Table.Body items={entries}>
									{(entry) => (
										<Table.Row
											id={entry.character.id}
											/*
											  Your own row, marked so you can find yourself in a
											  list sorted by level rather than by name.

											  Painted on the CELLS, not the row: HeroUI puts the
											  background on every `<td>`, so a background on the
											  `<tr>` sits behind them and shows nothing.
											*/
											className={
												isOwnCharacter(entry, ownMemberId)
													? '[&>td]:bg-accent-soft'
													: undefined
											}
										>
											{columns.map((column) => (
												<Table.Cell key={column.id} className={column.className}>
													{column.cell(entry)}
												</Table.Cell>
											))}
										</Table.Row>
									)}
								</Table.Body>
							</Table.Content>
						</Table.ScrollContainer>
					</Table>
				)}

				{/*
				  The way out to the admin list, mirroring the rotation card below.

				  Admins only — a member following it lands on a page the route guard
				  bounces them off — and it sits with the character LIST because that
				  is what it manages: everybody's attributes and names, plus the
				  players who have no Telegram account at all.
				*/}
				{isAdmin && loaded && (
					<p>
						<Link
							to="/admin/people"
							className="text-sm text-link underline underline-offset-2"
						>
							Управление составом
						</Link>
					</p>
				)}

				{/*
          The one call to action on this page, and it is only here when it can
          do something. Rendered after the list rather than before it: someone
          who already has a character never sees it, and someone who does not
          has just read a list of everyone who does.
        */}
				{loaded && !hasOwnCharacter && (
					<Link to="/character" className={buttonVariants({ fullWidth: true })}>
						Добавь своего персонажа
					</Link>
				)}

				{/*
				  ⚠️ MOUNTED ONLY WHILE A TARGET IS SELECTED, which is what makes
				  «closed» mean unmounted and every piece of dialog state — the
				  typed message, the armed delete, the loaded history — reset
				  between openings. A permanently-mounted controlled modal keeps
				  the last character's history behind an `isOpen` flag, and it
				  flashes on the next open before the new fetch lands.
				*/}
				{giveTo && (
					<KarmaGiveDialog
						target={giveTo}
						remaining={
							entries?.find((entry) => entry.character.id === giveTo.characterId)
								?.karmaRemainingToday ?? 0
						}
						onClose={() => setGiveTo(null)}
						onGranted={(result) =>
							onKarmaChanged(result.characterId, result.total, result.remainingToday)
						}
					/>
				)}

				{historyFor && (
					<KarmaHistoryDialog
						target={historyFor}
						isAdmin={isAdmin}
						onClose={() => setHistoryFor(null)}
						/* A deletion lowers the total and leaves the viewer's own
						   daily allowance alone — see `applyKarma` on why the
						   third argument is absent rather than guessed. */
						onChanged={(characterId, total) => onKarmaChanged(characterId, total)}
					/>
				)}
			</Card.Content>
		</Card>
	);
}

/**
 * Whether a row is the viewer's own.
 *
 * ⚠️ THE `ownMemberId !== null` GUARD IS LOAD-BEARING, and a bare `===` was a
 * real bug here. Both sides became nullable when admins gained the ability to
 * create characters for players with no Telegram (DECISION-33): `ownMemberId`
 * is null while `/api/me` is still resolving, and an unowned character's
 * `memberId` is null forever — so `null === null` marks EVERY admin-created
 * character as «вы» and highlights it, on the first paint of every page load.
 */
function isOwnCharacter(entry: RosterEntry, ownMemberId: number | null): boolean {
	return ownMemberId !== null && entry.character.memberId === ownMemberId;
}

function CharacterIdentity({
	entry,
	isOwn,
	onOpenKarma,
}: {
	entry: RosterEntry;
	isOwn: boolean;
	onOpenKarma: () => void;
}) {
	const owner = ownerOf(entry);
	return (
		<div className="min-w-0">
			<div className="flex items-baseline gap-2">
				<span className="truncate font-medium">{entry.character.name}</span>
				{isOwn && (
					<Chip size="sm" variant="soft" color="accent" className="shrink-0">
						вы
					</Chip>
				)}
				{/*
				  ⚠️ AFTER «вы», NOT BEFORE IT, AND THE SAME COLOURS BY
				  CONSTRUCTION. The owner asked for the «вы» label's colours, so
				  the badge renders `chipVariants` rather than two copied hexes —
				  this app's two themes are deliberately different golds, and a
				  copy stops tracking either the moment a token moves.

				  ⚠️ DRAWN AT ZERO TOO (owner's call). It skipped zero at first,
				  which meant the one row you look at most — your own, with
				  nobody having given you any yet — had no karma on it at all.
				*/}
				<KarmaBadge
					karma={entry.karma}
					characterName={entry.character.name}
					isOwn={isOwn}
					onOpen={onOpenKarma}
				/>
			</div>
			{/*
			  The Telegram name and handle, under the character name, ON SEPARATE
			  LINES (owner's call, 2026-08-13).

			  ⚠️ THEY WERE «Дмитрий · @dmitry» ON ONE LINE, and that line is what
			  sets this column's minimum width — the name above it truncates, but
			  a nowrap identity line does not shrink, so it decided how much room
			  the whole table needed. Stacking costs a line of height, which a
			  list scrolled vertically has, and buys back horizontal space, which
			  a four-column table on a phone does not. Latin-script handles stay
			  verbatim (PRD §7).

			  Either half may be missing — Telegram guarantees neither — so each
			  renders independently rather than sharing a separator.
			*/}
			{owner.name && <div className="truncate text-xs text-muted">{owner.name}</div>}
			{owner.handle && <div className="truncate text-xs text-muted">{owner.handle}</div>}
			{!owner.name && !owner.handle && (
				/* An admin-created character for a player with no Telegram
				   (DECISION-33). Said outright rather than left as a blank line:
				   an empty slot where every other row has a name reads as data
				   that failed to load. */
				<div className="truncate text-xs text-muted">без Telegram</div>
			)}
		</div>
	);
}

function LevelValue({
	entry,
	className,
}: {
	entry: RosterEntry;
	className?: string;
}) {
	const level = levelOf(entry);
	// «—», never 0. An empty field is not a level.
	return <span className={className}>{level === null ? "—" : level}</span>;
}

function RoleValue({ entry }: { entry: RosterEntry }) {
	const role = roleOf(entry);
	// Same dash as everywhere else: nobody's role is the empty string, and «—»
	// says "not filled in" where a blank cell reads as a rendering fault.
	return role === null ? <span className="text-muted">—</span> : <span>{role}</span>;
}
